import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    globalLoggerToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    modalManagerToken,
} from "@/tokens";
import checkIsMobile from "@/utils/checkIsMobile";
import { WISHLIST_PAGE_PATHNAME } from "@apps/wishlist/constants/constants";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { subscribeToSdkMethod } from "@modules/sdk/helpers";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { filter, lastValueFrom, switchMap, take, tap } from "rxjs";
import { assert } from "superstruct";
import {
    container,
    inject,
    injectable,
    instanceCachingFactory,
} from "tsyringe";

import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import {
    AddToWishlistButtonWidgetDefinition,
    AddToWishlistProductCardWidgetDefinition,
    AddToWishlistProductPageWidgetDefinition,
    GoToWishlistPageHeaderWidgetDefinition,
    SaveForLaterModalWidgetDefinition,
    SaveWishlistModalWidgetDefinition,
    SharedWishlistPageWidgetDefinition,
    WishlistDrawerWidgetDefinition,
    WishlistPageWidgetDefinition,
    WishlistsItemsTotalCountInlineWidgetDefinition,
    WishlistsManageApproversModalWidgetDefinition,
    WishlistsWidgetDefinition,
} from "./definitions";
import { WishlistsStorefrontValuesBrandingEntity } from "./entities/WishlistsStorefrontValuesBrandingEntity";
import { WishlistsQuery } from "./queries/WishlistsQuery";
import { WishlistsService } from "./services/WishlistsService";
import WishlistsStorefrontValuesService from "./services/WishlistsStorefrontValuesService";
import {
    wishlistApplicationLoggerToken,
    wishlistAuthApiClientToken,
} from "./tokens";
import { WISHLIST_BRANDING_LAUNCHER_VISIBILITY_TYPES } from "./types";
import AddToWishlistProductCardWidgetInjector from "./widgets/add_to_wishlist_product_card_widget/AddToWishlistProductCardWidgetInjector";
import AddToWishlistProductPageWidgetInjector from "./widgets/add_to_wishlist_product_page_widget/AddToWishlistProductPageWidgetInjector";
import GoToWishlistPageHeaderWidgetInjector from "./widgets/go_to_wishlist_page_header_widget/GoToWishlistPageHeaderWidgetInjector";
import SaveForLaterModalWidgetInjector from "./widgets/save_for_later_button_widget/SaveForLaterModalWidgetInjector";
import SaveWishlistModalWidgetInjector from "./widgets/save_wishlist_modal_widget/SaveWishlistModalWidgetInjector";
import SharedWishlistPageWidgetInjector from "./widgets/shared_wishlist_page_widget/SharedWishlistPageWidgetInjector";
import WishlistDrawerWidgetInjector from "./widgets/wishlist_drawer_widget/WishlistDrawerWidgetInjector";
import WishlistPageWidgetInjector from "./widgets/wishlist_page_widget/WishlistPageWidgetInjector";
import WishlistsItemsTotalCountInlineWidgetInjector from "./widgets/wishlists_inline_widgets/wishlists_items_total_count_inline_widget/WishlistsItemsTotalCountInlineWidgetInjector";
import WishlistsManageApproversModalWidgetInjector from "./widgets/wishlists_manage_approvers_modal_widget/WishlistsManageApproversModalWidgetInjector";
import WishlistsWidgetInjector from "./widgets/wishlists_widget/WishlistsWidgetInjector";
import {
    WishlistApplicationConfigInterface,
    WishlistApplicationConfigSchema,
    WishlistApplicationConfigToken,
} from "./WishlistApplicationConfig";
import { WISHLISTS_SDK_METHODS } from "./wishlists_sdk/constants";
import CreateWishlistItemSdkMethodPayload from "./wishlists_sdk/methods/CreateWishlistItemSdkMethodPayload";
import DeleteWishlistItemSdkMethodPayload from "./wishlists_sdk/methods/DeleteWishlistItemSdkMethodPayload";
import GetWishlistsStateKitSdkMethodPayload from "./wishlists_sdk/methods/GetWishlistsStateKitSdkMethodPayload";
import GetWishlistItemsSdkMethodPayload from "./wishlists_sdk/methods/GetWishlistItemsSdkMethodPayload";
import GetWishlistsSdkMethodPayload from "./wishlists_sdk/methods/GetWishlistsSdkMethodPayload";
import { WishlistsStateKit } from "./wishlists_sdk/state_kit/WishlistsStateKit";
import { mapWishlistItemToSdkDto } from "./wishlists_sdk/utils/mapWishlistItemToSdkDto";
import { mapWishlistToSdkDto } from "./wishlists_sdk/utils/mapWishlistToSdkDto";

@injectable()
export class WishlistApplication implements ApplicationInterface<object> {
    widgets = [];
    public exports = {};

    constructor(
        @inject(WishlistApplicationConfigToken)
        public readonly config: WishlistApplicationConfigInterface,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(DynamicImportModule)
        private readonly dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector,
        @inject(AttributesObserver)
        private readonly attributesObserver: AttributesObserver
    ) {
        assert(config, WishlistApplicationConfigSchema);

        const dynamicImportManager =
            dynamicImportModule.exports.dynamicImportManager;

        dynamicImportManager.register(AddToWishlistButtonWidgetDefinition);
        dynamicImportManager.register(AddToWishlistProductCardWidgetDefinition);
        dynamicImportManager.register(AddToWishlistProductPageWidgetDefinition);
        dynamicImportManager.register(GoToWishlistPageHeaderWidgetDefinition);
        dynamicImportManager.register(
            WishlistsManageApproversModalWidgetDefinition
        );
        dynamicImportManager.register(SaveWishlistModalWidgetDefinition);
        dynamicImportManager.register(SharedWishlistPageWidgetDefinition);
        dynamicImportManager.register(WishlistDrawerWidgetDefinition);
        dynamicImportManager.register(WishlistPageWidgetDefinition);
        dynamicImportManager.register(WishlistsWidgetDefinition);
        dynamicImportManager.register(
            WishlistsItemsTotalCountInlineWidgetDefinition
        );
        dynamicImportManager.register(SaveForLaterModalWidgetDefinition);
    }

    init(): this {
        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );
        const logger = new MinilogLogger("wishlist");

        const isDesignMode =
            this.commonModule.exports.designModeProvider.isDesignMode();

        const eventsCollector =
            analyticEventsCollectorModule.exports.eventsCollector;
        const analyticsEventsFactory =
            analyticEventsCollectorModule.exports.eventsFactory;
        const modalManager = container.resolve(modalManagerToken);

        container.registerInstance(wishlistApplicationLoggerToken, logger);
        container.register(wishlistAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/wishlist-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode || false,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        const wishlistsService = container.resolve(WishlistsService);
        const wishlistsQuery = container.resolve(WishlistsQuery);

        // Subscribes to SDK methods should be placed before SDK module registration
        subscribeToSdkMethod<CreateWishlistItemSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.CREATE_WISHLIST_ITEM,
            ({ productId, variantId, wishlistId, onSuccess, onError }) => {
                lastValueFrom(
                    wishlistsService.addItem({
                        productId,
                        variantId,
                        wishlistId,
                    })
                )
                    .then(() => onSuccess())
                    .catch((error: Error) => onError(error));
            }
        );

        subscribeToSdkMethod<DeleteWishlistItemSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.DELETE_WISHLIST_ITEM,
            ({ productId, variantId, wishlistId, onSuccess, onError }) => {
                lastValueFrom(
                    wishlistsService.deleteItem({
                        wishlistId,
                        productId,
                        variantId,
                    })
                )
                    .then(() => onSuccess())
                    .catch((error: Error) => onError(error));
            }
        );

        subscribeToSdkMethod<GetWishlistsSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.GET_WISHLISTS,
            ({ onSuccess, onError }) => {
                lastValueFrom(
                    wishlistsService.getWishlists().pipe(
                        switchMap(() => wishlistsQuery.selectAll()),
                        take(1)
                    )
                )
                    .then((wishlists) => {
                        const result = wishlists.map(mapWishlistToSdkDto);
                        onSuccess(result);
                    })
                    .catch((error: Error) => onError(error));
            }
        );

        subscribeToSdkMethod<GetWishlistItemsSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.GET_WISHLIST_ITEMS,
            ({ wishlistId, onSuccess, onError }) => {
                const selectWishlist = () => {
                    return wishlistId
                        ? wishlistsQuery.selectEntity(wishlistId)
                        : wishlistsQuery.selectFirst();
                };

                lastValueFrom(
                    wishlistsService.getWishlists().pipe(
                        switchMap(() => selectWishlist()),
                        take(1)
                    )
                )
                    .then((wishlist) => {
                        if (!wishlist) {
                            onError(new Error("Wishlist not found"));
                            return;
                        }
                        const result = wishlist.items.map(mapWishlistItemToSdkDto);
                        onSuccess(result);
                    })
                    .catch((error: Error) => onError(error));
            }
        );

        subscribeToSdkMethod<GetWishlistsStateKitSdkMethodPayload>(
            WISHLISTS_SDK_METHODS.GET_WISHLISTS_STATE_KIT,
            ({ onSuccess }) => {
                const stateKit = new WishlistsStateKit(
                    wishlistsQuery,
                    wishlistsService
                );

                onSuccess(stateKit);
            }
        );

        // Prepare SDK module for dynamic import
        void (async () => {
            const { sdkDynamicModulesManager, sdkMethodsCollector, sdk } =
                container.resolve(SdkModule).exports;

            const WishlistsSdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.WISHLISTS
            );
            const wishlistsSdk = new WishlistsSdk(sdkMethodsCollector);
            sdk.registerModule(SDK_MODULES.WISHLISTS, wishlistsSdk);
        })();

        logger.debug("WishlistApplication is initialized", {
            ...this.config,
        });

        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const isWishlistAppEnabled =
            growaveConfiguration.availableApps.includes(App.Wishlist) ||
            isDesignMode;

        const locationController =
            container.resolve(CommonModule).exports.locationController;
        const pathName = locationController.getPathname();

        const wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );

        const wishlistBranding$ =
            wishlistsStorefrontValuesService.selectWishlistsBranding();

        // Injectors
        const wishlistsWidgetInjector = new WishlistsWidgetInjector(
            this.config.wishlistsWidgetPlaceholder,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const wishlistDrawerWidgetInjector = new WishlistDrawerWidgetInjector(
            this.config.drawerWidgetPlaceholderSelector,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector,
            this.attributesObserver
        );

        const addToWishlistProductPageWidgetInjector =
            new AddToWishlistProductPageWidgetInjector(
                this.config.addToWishlistProductPageButtonSelector,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector,
                this.attributesObserver
            );

        const addToWishlistProductCardWidgetInjector =
            new AddToWishlistProductCardWidgetInjector(
                this.config.addToWishlistProductCardSelector,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const goToWishlistPageHeaderWidgetInjector =
            new GoToWishlistPageHeaderWidgetInjector(
                this.config.goToWishlistPageHeaderWidgetInjector,
                logger,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const wishlistPageWidgetInjector = new WishlistPageWidgetInjector(
            this.config.wishlistPageWidgetPlaceholderSelector,
            logger,
            modalManager,
            eventsCollector,
            analyticsEventsFactory,
            this.dynamicImportModule.exports.dynamicImportManager,
            this.mutationObserverInjector
        );

        const sharedWishlistPageWidgetInjector =
            new SharedWishlistPageWidgetInjector(
                this.config.sharedWishlistPageWidgetPlaceholderSelector,
                logger,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const saveWishlistModalWidgetInjector =
            new SaveWishlistModalWidgetInjector(
                this.config.saveWishlistModalWidgetPlaceholderSelector,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const wishlistsManageApproversModalWidgetInjector =
            new WishlistsManageApproversModalWidgetInjector(
                this.config.wishlistsManageApproversModalWidgetPlaceholderSelector,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const wishlistsItemsTotalCountInlineWidgetInjector =
            new WishlistsItemsTotalCountInlineWidgetInjector(
                this.config.wishlistsItemsTotalCountInlineWidgetPlaceholderSelector,
                logger,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        const saveForLaterModalWidgetInjector =
            new SaveForLaterModalWidgetInjector(
                this.config.saveForLaterModalWidgetPlaceholderSelector,
                logger,
                modalManager,
                eventsCollector,
                analyticsEventsFactory,
                this.dynamicImportModule.exports.dynamicImportManager,
                this.mutationObserverInjector
            );

        if (
            isWishlistAppEnabled &&
            !pathName.includes(WISHLIST_PAGE_PATHNAME)
        ) {
            wishlistBranding$
                .pipe(
                    filter(
                        (
                            branding
                        ): branding is WishlistsStorefrontValuesBrandingEntity =>
                            !!branding
                    ),
                    take(1),
                    tap((wishlistBranding) => {
                        const isMobile = checkIsMobile();
                        const settings =
                            wishlistBranding.wishlistDrawerAndPageSettings;

                        const isDrawerVisible = (() => {
                            switch (settings.device) {
                                case WISHLIST_BRANDING_LAUNCHER_VISIBILITY_TYPES.allDevices:
                                    return true;
                                case WISHLIST_BRANDING_LAUNCHER_VISIBILITY_TYPES.desktopOnly:
                                    return !isMobile;
                                case WISHLIST_BRANDING_LAUNCHER_VISIBILITY_TYPES.mobileOnly:
                                    return isMobile;
                                case WISHLIST_BRANDING_LAUNCHER_VISIBILITY_TYPES.hide:
                                default:
                                    return false;
                            }
                        })();

                        if (isDrawerVisible) {
                            wishlistDrawerWidgetInjector.start();
                        }
                    })
                )
                .subscribe();
        }

        if (isWishlistAppEnabled) {
            wishlistsWidgetInjector.start();
            addToWishlistProductPageWidgetInjector.start();
            addToWishlistProductCardWidgetInjector.start();
            goToWishlistPageHeaderWidgetInjector.start();
            sharedWishlistPageWidgetInjector.start();
            saveWishlistModalWidgetInjector.start();
            wishlistsManageApproversModalWidgetInjector.start();
            wishlistsItemsTotalCountInlineWidgetInjector.start();
            saveForLaterModalWidgetInjector.start();
        }

        wishlistPageWidgetInjector.start();

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.Wishlist,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}
